import { createRoot, useState, useRef } from '@wordpress/element';

const STATUS_STEPS = [ 'pending', 'processing', 'completed' ];

const STATUS_META = {
	pending:    { label: 'Order Received',  step: 0 },
	processing: { label: 'Processing',      step: 1 },
	'on-hold':  { label: 'On Hold',         step: 1 },
	completed:  { label: 'Completed',       step: 2 },
	cancelled:  { label: 'Cancelled',       step: -1 },
	refunded:   { label: 'Refunded',        step: -1 },
	failed:     { label: 'Payment Failed',  step: -1 },
};

function ProgressBar( { status } ) {
	const meta        = STATUS_META[ status ] ?? { step: 1 };
	const isTerminal  = meta.step === -1;
	const currentStep = isTerminal ? 0 : meta.step;

	return (
		<div className="sienci-order-status__progress" aria-hidden="true">
			{ STATUS_STEPS.map( ( step, i ) => (
				<div key={ step } className="sienci-order-status__progress-step">
					<div
						className={ [
							'sienci-order-status__progress-dot',
							i <= currentStep && ! isTerminal ? 'is-done' : '',
							i === currentStep && ! isTerminal ? 'is-current' : '',
						].filter( Boolean ).join( ' ' ) }
					/>
					{ i < STATUS_STEPS.length - 1 && (
						<div
							className={ [
								'sienci-order-status__progress-line',
								i < currentStep && ! isTerminal ? 'is-done' : '',
							].filter( Boolean ).join( ' ' ) }
						/>
					) }
				</div>
			) ) }
		</div>
	);
}

function TrackingCard( { tracking } ) {
	if ( ! tracking?.number ) return null;

	return (
		<div className="sienci-order-status__tracking">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
				<path d="M6.5 3c-1.051 0-2.093.04-3.125.117A1.49 1.49 0 0 0 2 4.607V10.5h9V4.606c0-.771-.59-1.43-1.375-1.489A41.568 41.568 0 0 0 6.5 3zM2 12v2.5A1.5 1.5 0 0 0 3.5 16h.041a3 3 0 0 1 5.918 0h.791a.75.75 0 0 0 .75-.75V12H2z" />
				<path d="M6.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM13.25 5a.75.75 0 0 0-.75.75v8.514a3.001 3.001 0 0 1 4.893 1.44c.37-.275.607-.71.607-1.204V9.75A3.75 3.75 0 0 0 14.25 6h-1v-.25zM14.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
			</svg>
			<div className="sienci-order-status__tracking-info">
				{ tracking.provider && (
					<span className="sienci-order-status__tracking-provider">{ tracking.provider }</span>
				) }
				{ tracking.url ? (
					<a
						href={ tracking.url }
						className="sienci-order-status__tracking-number"
						target="_blank"
						rel="noopener noreferrer"
					>
						{ tracking.number }
					</a>
				) : (
					<span className="sienci-order-status__tracking-number">{ tracking.number }</span>
				) }
				{ tracking.date && (
					<span className="sienci-order-status__tracking-date">Shipped { tracking.date }</span>
				) }
				{ tracking.delivered === true && (
					<span className="sienci-order-status__tracking-delivered">
						Delivered{ tracking.delivered_at ? ` ${ tracking.delivered_at }` : '' }
					</span>
				) }
				{ tracking.delivered === false && tracking.delivery_note && (
					<span className="sienci-order-status__tracking-date">{ tracking.delivery_note }</span>
				) }
			</div>
		</div>
	);
}

function ShipmentGroup( { group } ) {
	return (
		<div className="sienci-order-status__group">
			<span className="sienci-order-status__item-badge is-shipped">Shipped</span>
			<ul className="sienci-order-status__group-items">
				{ group.items.map( ( name, i ) => (
					<li key={ i } className="sienci-order-status__group-item">{ name }</li>
				) ) }
			</ul>
			<TrackingCard tracking={ group.tracking } />
		</div>
	);
}

function ItemNotes( { notes } ) {
	if ( ! notes?.length ) return null;
	return (
		<div className="sienci-order-status__item-notes">
			{ notes.map( ( entry, i ) => (
				<div key={ i } className="sienci-order-status__delay-note" role="note">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
						<path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
					</svg>
					<div className="sienci-order-status__delay-note-body">
						{ entry.date && <span className="sienci-order-status__delay-note-date">{ entry.date }</span> }
						<p className="sienci-order-status__delay-note-text">{ entry.note }</p>
					</div>
				</div>
			) ) }
		</div>
	);
}

function ItemList( { items, badgeClass, badgeLabel } ) {
	if ( ! items?.length ) return null;

	// Items may be strings (unknown) or objects { name, estimated_ship_date, notes } (pending).
	return (
		<div className="sienci-order-status__group">
			<span className={ `sienci-order-status__item-badge ${ badgeClass }` }>{ badgeLabel }</span>
			<ul className="sienci-order-status__group-items">
				{ items.map( ( item, i ) => {
					const name     = typeof item === 'string' ? item : item.name;
					const estimate = typeof item === 'object' ? item.estimated_ship_date : null;
					const notes    = typeof item === 'object' ? item.notes?.filter( n => ! n.resolved ) : null;
					return (
						<li key={ i } className="sienci-order-status__group-item-wrap">
							<div className="sienci-order-status__group-item">
								{ name }
								{ estimate && (
									<span className="sienci-order-status__item-estimate">
										Est. ships by { estimate }
									</span>
								) }
							</div>
							<ItemNotes notes={ notes } />
						</li>
					);
				} ) }
			</ul>
		</div>
	);
}

function PartialTrackingNotice( { tracking } ) {
	if ( ! tracking?.number ) return null;

	return (
		<div className="sienci-order-status__partial-notice">
			<p className="sienci-order-status__partial-notice-text">
				A shipment has gone out for part of this order, but we can't confirm exactly which items it included. The tracking number on file is below — contact support if you need to know which items are in it.
			</p>
			<TrackingCard tracking={ tracking } />
		</div>
	);
}

function AlertBanner( { alert } ) {
	if ( ! alert ) return null;

	return (
		<div className={ `sienci-order-status__alert is-${ alert.severity }` } role="alert">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
			</svg>
			<div className="sienci-order-status__alert-body">
				<p className="sienci-order-status__alert-message">{ alert.message }</p>
				{ alert.action?.url && (
					<a
						href={ alert.action.url }
						className="sienci-order-status__alert-action"
						target={ alert.action.type === 'contact_support' ? '_blank' : undefined }
						rel={ alert.action.type === 'contact_support' ? 'noopener noreferrer' : undefined }
					>
						{ alert.action.label }
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13" aria-hidden="true">
							<path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8z" clipRule="evenodd" />
						</svg>
					</a>
				) }
			</div>
		</div>
	);
}

function OrderResultSkeleton() {
	return (
		<div className="sienci-order-status__result" aria-hidden="true">
			<div className="sienci-order-status__result-header">
				<div className="sienci-order-status__skeleton-header-lines">
					<span className="ssf-skeleton sienci-order-status__skeleton-bar--title" />
					<span className="ssf-skeleton sienci-order-status__skeleton-bar--date" />
				</div>
				<span className="ssf-skeleton sienci-order-status__skeleton-bar--badge" />
			</div>

			<div className="sienci-order-status__progress">
				{ [ 0, 1, 2 ].map( ( i ) => (
					<div key={ i } className="sienci-order-status__progress-step">
						<span className="ssf-skeleton sienci-order-status__skeleton-dot" />
						{ i < 2 && <span className="ssf-skeleton sienci-order-status__skeleton-line" /> }
					</div>
				) ) }
			</div>

			<div className="sienci-order-status__items">
				{ [ 0, 1 ].map( ( i ) => (
					<div key={ i } className="sienci-order-status__group">
						<span className="ssf-skeleton sienci-order-status__skeleton-bar--item" />
						<span className="ssf-skeleton sienci-order-status__skeleton-bar--item-short" />
						<span className="ssf-skeleton sienci-order-status__skeleton-tracking" />
					</div>
				) ) }
			</div>
		</div>
	);
}

function OrderResult( { data } ) {
	const meta         = STATUS_META[ data.status ] ?? {};
	const isTerminal   = meta.step === -1;
	const statusLabel  = data.status_label ?? meta.label ?? data.status;

	return (
		<div className="sienci-order-status__result">
			<div className="sienci-order-status__result-header">
				<div>
					<p className="sienci-order-status__order-id">Order #{ data.id }</p>
					{ data.order_date && (
						<p className="sienci-order-status__order-date">Placed { data.order_date }</p>
					) }
				</div>
				<span className={ [ 'sienci-order-status__badge', isTerminal ? 'is-terminal' : '', data.alert ? `is-${ data.alert.severity }` : '' ].filter( Boolean ).join( ' ' ) }>
					{ statusLabel }
				</span>
			</div>

			<AlertBanner alert={ data.alert } />

			{ /* Legacy order-level notes for orders created before per-item notes */ }
			{ data.delay_notes?.length > 0 && (
				<div className="sienci-order-status__delay-notes">
					{ data.delay_notes.map( ( entry, i ) => (
						<div key={ i } className="sienci-order-status__delay-note" role="note">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
								<path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
							</svg>
							<div className="sienci-order-status__delay-note-body">
								{ entry.date && <span className="sienci-order-status__delay-note-date">{ entry.date }</span> }
								<p className="sienci-order-status__delay-note-text">{ entry.note }</p>
							</div>
						</div>
					) ) }
				</div>
			) }

			{ ! isTerminal && <ProgressBar status={ data.status } /> }

			{ data.shipped_groups?.length > 0 && (
				<div className="sienci-order-status__items">
					{ data.shipped_groups.map( ( group, i ) => (
						<ShipmentGroup key={ i } group={ group } />
					) ) }
				</div>
			) }

			{ data.pending_items?.length > 0 && (
				<div className="sienci-order-status__items">
					<ItemList items={ data.pending_items } badgeClass="is-pending" badgeLabel="Not Yet Shipped" />
				</div>
			) }

			{ data.unknown_items?.length > 0 && (
				<div className="sienci-order-status__items">
					<ItemList items={ data.unknown_items } badgeClass="is-unknown" badgeLabel="Status Unavailable" />
				</div>
			) }

			<PartialTrackingNotice tracking={ data.partial_tracking } />
		</div>
	);
}

function OrderStatusLookup() {
	const [ orderId, setOrderId ] = useState( '' );
	const [ loading, setLoading ] = useState( false );
	const [ result, setResult ]   = useState( null );
	const [ error, setError ]     = useState( null );
	const inputRef                = useRef( null );

	const restUrl = window.sienciShipstationConfig?.restUrl ?? '/wp-json/sienci/v1/';

	const handleSubmit = ( e ) => {
		e.preventDefault();

		const id = orderId.trim();
		if ( ! id || isNaN( id ) ) {
			setError( 'Please enter a valid order number.' );
			return;
		}

		setLoading( true );
		setError( null );
		setResult( null );

		fetch( restUrl + 'order-status/' + encodeURIComponent( id ) )
			.then( ( r ) => r.json() )
			.then( ( data ) => {
				if ( data?.code ) {
					setError( 'No order found with that number. Please double-check and try again.' );
				} else {
					setResult( data );
				}
			} )
			.catch( () => setError( 'Something went wrong. Please try again.' ) )
			.finally( () => setLoading( false ) );
	};

	return (
		<div className="sienci-order-status__inner">
			<form className="sienci-order-status__form" onSubmit={ handleSubmit } noValidate>
				<label className="sienci-order-status__label" htmlFor="sienci-order-id">
					Order Number
				</label>
				<div className="sienci-order-status__input-row">
					<input
						ref={ inputRef }
						id="sienci-order-id"
						type="text"
						inputMode="numeric"
						className="sienci-order-status__input"
						placeholder="e.g. 10482"
						value={ orderId }
						onChange={ ( e ) => setOrderId( e.target.value ) }
						aria-describedby={ error ? 'sienci-order-error' : undefined }
						disabled={ loading }
					/>
					<button
						type="submit"
						className="sienci-order-status__button"
						disabled={ loading || ! orderId.trim() }
					>
						{ loading ? 'Looking up…' : 'Check Status' }
					</button>
				</div>
				{ error && (
					<p id="sienci-order-error" className="sienci-order-status__error" role="alert">
						{ error }
					</p>
				) }
			</form>

			{ loading && <OrderResultSkeleton /> }
			{ ! loading && result && <OrderResult data={ result } /> }
		</div>
	);
}

document.querySelectorAll( '.sienci-order-status' ).forEach( ( el ) => {
	createRoot( el ).render( <OrderStatusLookup /> );
} );
